import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

// Use unified date utils
import { moment } from '../../utils/dateUtils';

import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Divider,
  CircularProgress,
  List,
  ListItem,
} from '@mui/material';

import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CreateIcon from '@mui/icons-material/Create';
import EditIcon from '@mui/icons-material/Edit';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';

import proposalHistoryServices from '../../services/proposalHistoryServices';

// Change type icons and colors
const changeTypeConfig = {
  created: { icon: <CreateIcon fontSize="small" />, color: 'success', label: 'Created' },
  status_change: { icon: <CheckCircleIcon fontSize="small" />, color: 'warning', label: 'Status Change' },
  details_update: { icon: <EditIcon fontSize="small" />, color: 'info', label: 'Updated' },
  price_update: { icon: <AttachMoneyIcon fontSize="small" />, color: 'primary', label: 'Price Update' },
  scope_update: { icon: <AssignmentIcon fontSize="small" />, color: 'secondary', label: 'Scope Update' },
  signed: { icon: <CheckCircleIcon fontSize="small" />, color: 'success', label: 'Signed' },
  emailed: { icon: <EmailIcon fontSize="small" />, color: 'info', label: 'Emailed' },
};

// --- Diff helpers for array fields (warranties, discounts, workDescriptions, payments...) ---

const money = (v) => {
  const n = parseFloat(v);
  if (Number.isNaN(n)) return null;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
};

// Human-readable one-line label for an array item
const itemLabel = (item) => {
  if (item === null || typeof item !== 'object') return String(item);
  const base = item.description || item.name || item.methodName || `#${item.id ?? '?'}`;
  const extras = [];
  if (item.years) extras.push(`${item.years} yr`);
  if (item.date) extras.push(item.date);
  if (item.reference) extras.push(`Ref ${item.reference}`);
  return extras.length ? `${base} (${extras.join(', ')})` : base;
};

const itemPrice = (item) => {
  if (item === null || typeof item !== 'object') return null;
  if (item.price !== undefined) return money(item.price);
  if (item.amount !== undefined) return money(item.amount);
  return null;
};

// Compare two arrays of objects by id; returns { added, removed, changed, unchanged }
const diffArrays = (before, after) => {
  const prev = Array.isArray(before) ? before : [];
  const next = Array.isArray(after) ? after : [];
  const keyOf = (item, idx) => (item && typeof item === 'object' && item.id !== undefined ? String(item.id) : `idx-${idx}`);
  const prevMap = new Map(prev.map((item, idx) => [keyOf(item, idx), item]));
  const nextMap = new Map(next.map((item, idx) => [keyOf(item, idx), item]));

  const added = [];
  const removed = [];
  const changed = [];
  let unchanged = 0;

  for (const [key, item] of nextMap) {
    if (!prevMap.has(key)) {
      added.push(item);
    } else if (JSON.stringify(prevMap.get(key)) !== JSON.stringify(item)) {
      changed.push({ before: prevMap.get(key), after: item });
    } else {
      unchanged++;
    }
  }
  for (const [key, item] of prevMap) {
    if (!nextMap.has(key)) removed.push(item);
  }
  return { added, removed, changed, unchanged };
};

const DiffLine = ({ sign, color, label, price, priceBefore }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.25 }}>
    <Typography component="span" sx={{ fontWeight: 700, color, width: 14, flexShrink: 0, fontSize: 13 }}>
      {sign}
    </Typography>
    <Typography component="span" sx={{ fontSize: 13, color: 'text.primary', flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
      {label}
    </Typography>
    {(price || priceBefore) && (
      <Typography component="span" sx={{ fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0 }}>
        {priceBefore && (
          <Typography component="span" sx={{ fontSize: 13, color: 'error.main', textDecoration: 'line-through', mr: 0.5 }}>
            {priceBefore}
          </Typography>
        )}
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color }}>
          {price}
        </Typography>
      </Typography>
    )}
  </Box>
);

// Render an array field change as added/removed/changed lines
const ArrayFieldDiff = ({ before, after }) => {
  const { added, removed, changed, unchanged } = diffArrays(before, after);

  if (added.length === 0 && removed.length === 0 && changed.length === 0) {
    // Same items, different order — the only way to get here with a recorded change
    const reordered = JSON.stringify(before || []) !== JSON.stringify(after || []);
    return (
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
        {reordered ? 'Items reordered (same content)' : 'No visible changes'}
      </Typography>
    );
  }

  return (
    <Box>
      {removed.map((item, i) => (
        <DiffLine key={`r${i}`} sign="−" color="error.main" label={itemLabel(item)} price={itemPrice(item)} />
      ))}
      {added.map((item, i) => (
        <DiffLine key={`a${i}`} sign="+" color="success.main" label={itemLabel(item)} price={itemPrice(item)} />
      ))}
      {changed.map(({ before: b, after: a }, i) => {
        const pb = itemPrice(b);
        const pa = itemPrice(a);
        const labelChanged = itemLabel(b) !== itemLabel(a);
        return (
          <Box key={`c${i}`}>
            {labelChanged && <DiffLine sign="−" color="error.main" label={itemLabel(b)} price={pb} />}
            <DiffLine
              sign={labelChanged ? '+' : '~'}
              color={labelChanged ? 'success.main' : 'warning.main'}
              label={itemLabel(a)}
              price={pa}
              priceBefore={!labelChanged && pb !== pa ? pb : null}
            />
          </Box>
        );
      })}
      {unchanged > 0 && (
        <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.5 }}>
          {unchanged} unchanged item{unchanged > 1 ? 's' : ''} not shown
        </Typography>
      )}
    </Box>
  );
};

// Scalar (string/number/bool) before → after
const ScalarFieldDiff = ({ before, after }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
    <Typography sx={{ fontSize: 13, color: 'error.main', textDecoration: 'line-through', overflowWrap: 'anywhere' }}>
      {formatValue(before)}
    </Typography>
    <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>→</Typography>
    <Typography sx={{ fontSize: 13, color: 'success.main', fontWeight: 600, overflowWrap: 'anywhere' }}>
      {formatValue(after)}
    </Typography>
  </Box>
);

const FieldDiff = ({ field, before, after }) => {
  const isArrayField = Array.isArray(before) || Array.isArray(after);
  return (
    <Box sx={{ mt: 1, p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'capitalize', color: 'text.secondary', display: 'block', mb: 0.5 }}>
        {field.replace(/([A-Z])/g, ' $1').trim()}
      </Typography>
      {isArrayField
        ? <ArrayFieldDiff before={before} after={after} />
        : <ScalarFieldDiff before={before} after={after} />}
    </Box>
  );
};

const HistoryEntry = ({ entry }) => {
  const config = changeTypeConfig[entry.changeType] || changeTypeConfig.details_update;
  const hasDetails = entry.changedFields && entry.changedFields.length > 0;
  // Diffs are compact one-liners now, so show them right away
  const [expanded, setExpanded] = useState(hasDetails);

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {/* Icon */}
        <Box 
          sx={{ 
            width: 36, 
            height: 36, 
            borderRadius: '50%', 
            backgroundColor: `${config.color}.light`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: `${config.color}.main`,
            flexShrink: 0,
          }}
        >
          {config.icon}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Chip 
                  label={config.label} 
                  size="small" 
                  color={config.color}
                  variant="outlined"
                  sx={{ height: 22 }}
                />
                {entry.changedBy && (
                  <Typography variant="caption" color="textSecondary">
                    by {entry.changedBy.fullName}
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {entry.description}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                {moment(entry.createdAt).tz('America/Los_Angeles').format('MMM D, YYYY h:mm A')}
              </Typography>
              {hasDetails && (
                <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                  {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              )}
            </Box>
          </Box>

          <Collapse in={expanded}>
            <Divider sx={{ my: 1.5 }} />
            <Box>
              {entry.changedFields?.map((field, idx) => (
                <FieldDiff
                  key={idx}
                  field={field}
                  before={entry.previousData?.[field]}
                  after={entry.newData?.[field]}
                />
              ))}
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Paper>
  );
};

// Format value for display
const formatValue = (value) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString();
  if (Array.isArray(value)) {
    if (value.length === 0) return 'Empty';
    return `${value.length} items`;
  }
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  if (typeof value === 'string' && value.length > 100) {
    return value.substring(0, 100) + '...';
  }
  return String(value);
};

const ProposalHistory = ({ proposalId, open, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && proposalId) {
      loadHistory();
    }
  }, [open, proposalId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const response = await proposalHistoryServices.getHistory(proposalId);
      if (response.status === 200) {
        setHistory(response.data);
      }
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Failed to load proposal history');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HistoryIcon />
        Proposal History
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : history.length === 0 ? (
          <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
            No history records found
          </Typography>
        ) : (
          <List disablePadding>
            {history.map((entry) => (
              <ListItem key={entry.id} disablePadding sx={{ display: 'block' }}>
                <HistoryEntry entry={entry} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProposalHistory;
